import { useEffect, useRef, useState } from "react";
import { formatDate2Digit } from "../lib/formatTime";
import type { Day } from "../types";

interface DaySelectorProps {
  days: Day[];
  activeDayId: string;
  handleDayChange: (dayId: string) => void;
}

export function DaySelector({ days, activeDayId, handleDayChange }: DaySelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeft(scrollLeft > 5);
      // Allow minor floating-point tolerances on high-DPI screens
      setShowRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: check scroll on mount and when days change
  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener("scroll", checkScroll);
      }
      window.removeEventListener("resize", checkScroll);
    };
  }, [days]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when activeDayId or days list changes
  useEffect(() => {
    if (activeDayId && containerRef.current) {
      const timer = setTimeout(() => {
        const container = containerRef.current;
        if (!container) return;
        const activeEl = document.getElementById(`day-filter-${activeDayId}`);
        if (activeEl) {
          const activeRect = activeEl.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const targetScrollLeft =
            container.scrollLeft +
            (activeRect.left - containerRect.left) -
            containerRect.width / 2 +
            activeRect.width / 2;

          container.scrollTo({
            left: targetScrollLeft,
            behavior: "smooth",
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeDayId, days]);

  const handleScroll = (direction: "left" | "right") => {
    if (containerRef.current) {
      const amount = direction === "left" ? -180 : 180;
      containerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  return (
    <div className="mb-4">
      <div className="font-['Unbounded'] text-[8px] font-extrabold uppercase tracking-widest text-[#9f98b5] mb-2 pl-0.5 hidden">
        Nap Kiválasztása
      </div>

      <div className="relative group">
        {/* Left Fade Gradient */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-background to-transparent pointer-events-none z-10 transition-opacity duration-300 ${
            showLeft ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* Right Fade Gradient */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-background to-transparent pointer-events-none z-10 transition-opacity duration-300 ${
            showRight ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* Left Arrow Button */}
        <button
          onClick={() => handleScroll("left")}
          className={`absolute left-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full border border-white/10 bg-[#222222]/90 text-purple flex items-center justify-center active:scale-90 shadow-[0_0_12px_rgba(0,0,0,0.6)] hover:bg-[#333333] hover:border-white/20 transition-all duration-300 ${
            showLeft
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-75 pointer-events-none"
          }`}
          type="button"
          aria-label="Görgetés balra"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="3"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Right Arrow Button */}
        <button
          onClick={() => handleScroll("right")}
          className={`absolute right-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full border border-white/10 bg-[#222222]/90 text-purple flex items-center justify-center active:scale-90 shadow-[0_0_12px_rgba(0,0,0,0.6)] hover:bg-[#333333] hover:border-white/20 transition-all duration-300 ${
            showRight
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-75 pointer-events-none"
          }`}
          type="button"
          aria-label="Görgetés jobbra"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="3"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div
          ref={containerRef}
          className="flex gap-3 items-center overflow-x-auto pb-1.5 day-row scrollbar-none scroll-smooth"
        >
          {days.map((day) => {
            const isActive = day.id === activeDayId;
            const formattedDate = formatDate2Digit(day.date);
            const dateClean = formattedDate.endsWith(".")
              ? formattedDate.slice(0, -1)
              : formattedDate;

            return (
              <button
                id={`day-filter-${day.id}`}
                key={day.id}
                onClick={() => handleDayChange(day.id)}
                className={`flex-shrink-0 min-w-[105px] min-h-[60px] px-3 py-1.5 rounded-xl border flex flex-col items-center justify-center cursor-pointer relative transition-all duration-300 ${
                  isActive
                    ? "bg-main border-main shadow-[0_0_20px_rgba(34,211,186,0.6)] text-[#222222]"
                    : "bg-[#222222]/60 border-white/5 hover:border-white/15 text-text"
                }`}
                type="button"
                role="button"
                aria-label={`Szűrés napra: ${day.name}`}
              >
                <span
                  className={`font-['Unbounded'] text-[11px] font-bold uppercase tracking-wide text-center px-1.5 leading-tight ${
                    isActive ? "text-[#222222]" : "text-white"
                  }`}
                >
                  {day.shortname}
                </span>
                <span
                  className={`font-['Unbounded'] text-[9px] font-semibold uppercase tracking-wider mt-0.5 ${
                    isActive ? "text-[#222222]/70" : "text-muted/70"
                  }`}
                >
                  {dateClean}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
